import { useCallback, useEffect, useRef, useState } from "react";

/**
 * キュー内で待機中の 1 エントリ。
 * callback 到着時は `contextKey + generation` の完全一致で解放する。
 */
type PendingPersistenceWait = {
  contextKey: string;
  generation: number;
  resolve: () => void;
  reject: (error: Error) => void;
};

/**
 * 1 回の send に紐づく相関トークン。
 *
 * 以下を同一トークンで突き合わせるために使う:
 * - キュー上の send 実行 (`runSerialized`)
 * - 後続コールバック (`onFinish` / `onError`)
 *
 * wait 解放には `contextKey` と `generation` の両一致が必要。
 */
export type PersistenceGenerationToken = {
  contextKey: string;
  generation: number;
};

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

/**
 * queue 無効化（chat 切替・リセット）による rejection かどうかを判定する。
 *
 * `runSerialized` が throw する "Chat context switched" を呼び出し側で識別し、
 * Bugsnag 通知が不要な rejection を swallow するために使う。
 */
export function isQueueInvalidationError(error: unknown): boolean {
  return error instanceof Error && error.message === "Chat context switched";
}

/**
 * send 実行を直列化し、wait の明示的な解放 API を提供する。
 *
 * 設計意図:
 * - `tokenGenerationRef` は同一 chat 内での callback と wait の相関を守る。
 * - `queueVersionRef` はコンテキスト遷移時にキュー実行を無効化する。
 *   （chat 切替、draft->persisted 置換、明示 invalidate）
 */
export function usePersistenceQueue(contextKey: string) {
  const pendingWaitsRef = useRef<PendingPersistenceWait[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const tokenGenerationRef = useRef(0);
  const queueVersionRef = useRef(0);
  const latestContextKeyRef = useRef(contextKey);

  /**
   * 現在コンテキストを同期する。
   * コンテキストが変わったときのみ、token 世代と queue 版を同時に進める。
   */
  const synchronizeContext = useCallback((nextContextKey: string) => {
    if (nextContextKey === latestContextKeyRef.current) return;
    tokenGenerationRef.current += 1;
    queueVersionRef.current += 1;
    latestContextKeyRef.current = nextContextKey;
  }, []);

  useEffect(() => {
    synchronizeContext(contextKey);
  }, [contextKey, synchronizeContext]);

  /**
   * render 時点で使える相関トークンを発行する。
   * useEffect 実行前でも新コンテキスト向け世代を先読みして返す。
   */
  const createGenerationToken = useCallback(
    (nextContextKey: string): PersistenceGenerationToken => {
      // 呼び出し側がこの render で新コンテキストを参照している場合、
      // useEffect より前でも stale token を出さないよう次世代を先読みする。
      const generation =
        nextContextKey === latestContextKeyRef.current
          ? tokenGenerationRef.current
          : tokenGenerationRef.current + 1;

      return {
        contextKey: nextContextKey,
        generation,
      };
    },
    [],
  );

  const enqueueWaitPromise = useCallback(
    (token: PersistenceGenerationToken) => {
      return new Promise<void>((resolve, reject) => {
        pendingWaitsRef.current.push({
          contextKey: token.contextKey,
          generation: token.generation,
          resolve,
          reject: (error: Error) => reject(error),
        });
        setPendingCount(pendingWaitsRef.current.length);
      });
    },
    [],
  );

  const resolveNextWait = useCallback((token: PersistenceGenerationToken) => {
    const index = pendingWaitsRef.current.findIndex(
      (wait) =>
        wait.contextKey === token.contextKey &&
        wait.generation === token.generation,
    );
    if (index < 0) return;
    const [next] = pendingWaitsRef.current.splice(index, 1);
    setPendingCount(pendingWaitsRef.current.length);
    next?.resolve();
  }, []);

  const rejectNextWait = useCallback(
    (token: PersistenceGenerationToken, error: unknown) => {
      const index = pendingWaitsRef.current.findIndex(
        (wait) =>
          wait.contextKey === token.contextKey &&
          wait.generation === token.generation,
      );
      if (index < 0) return;
      const [next] = pendingWaitsRef.current.splice(index, 1);
      setPendingCount(pendingWaitsRef.current.length);
      next?.reject(toError(error));
    },
    [],
  );

  const rejectAllWaits = useCallback((error: unknown) => {
    const normalizedError = toError(error);
    const pending = pendingWaitsRef.current.splice(
      0,
      pendingWaitsRef.current.length,
    );
    setPendingCount(0);
    for (const next of pending) {
      next.reject(normalizedError);
    }
  }, []);

  /**
   * callback の相関 token に対応する待機が、現在の queue にまだ残っているかを返す。
   * chat 切替や reset で無効化された古い callback の副作用を防ぐために使う。
   */
  const hasPendingWaitForToken = useCallback(
    (token: PersistenceGenerationToken) =>
      pendingWaitsRef.current.some(
        (wait) =>
          wait.contextKey === token.contextKey &&
          wait.generation === token.generation,
      ),
    [],
  );

  /**
   * context 置換（例: draft -> persisted）時に、後続キュー実行だけを失効させる。
   * wait 自体の reject は呼び出し側で制御する。
   */
  const markContextTransition = useCallback(() => {
    queueVersionRef.current += 1;
  }, []);

  /**
   * キューを強制無効化する。
   * 保留 wait を reject し、以降の queued run が fail-fast するよう queue 版を進める。
   */
  const invalidateQueue = useCallback(
    (error: unknown) => {
      rejectAllWaits(error);
      queueVersionRef.current += 1;
      queueRef.current = Promise.resolve();
    },
    [rejectAllWaits],
  );

  /**
   * send を直列化し、対応する wait が解放されるまで完了扱いにしない。
   */
  const runSerialized = useCallback(
    (token: PersistenceGenerationToken, runner: () => Promise<void>) => {
      synchronizeContext(token.contextKey);
      const capturedVersion = queueVersionRef.current;
      const run = queueRef.current.then(async () => {
        // 無効化済みコンテキストに属する queued run は即時失敗させる。
        if (capturedVersion !== queueVersionRef.current) {
          throw new Error("Chat context switched");
        }
        // 実行コンテキストを束縛する前に、render 反映を 1 tick 待つ。
        await Promise.resolve();
        const wait = enqueueWaitPromise(token);
        // runner 実行中に queue が無効化されると rejectAllWaits が wait を
        // reject するが、await wait に到達する前なので unhandled rejection になる。
        // 空の catch を先に付けて防ぐ（await wait 自体の reject は維持される）。
        wait.catch(() => {});
        try {
          await runner();
        } catch (error) {
          rejectNextWait(token, error);
          throw error;
        }
        await wait;
      });

      queueRef.current = run.catch(() => undefined);
      return run;
    },
    [enqueueWaitPromise, rejectNextWait, synchronizeContext],
  );

  return {
    createGenerationToken,
    pendingCount,
    resolveNextWait,
    rejectNextWait,
    rejectAllWaits,
    hasPendingWaitForToken,
    markContextTransition,
    invalidateQueue,
    runSerialized,
  };
}
